import { useState, useEffect } from "react";
import { Offer, Product, TenantConfig } from "../types";
import { Calendar, Sparkles, UtensilsCrossed, Clock } from "lucide-react";
import { OfferCard } from "./OfferCard";
import { ProductCard } from "./ProductCard";
import { isDateWorkingDay } from "../lib/date";

interface WeeklyOfferDay {
    date: string;
    day_name: string;
    offers: Offer[];
}

interface WeeklyOffersProps {
    upcomingOffers: WeeklyOfferDay[];
    products: Product[];
    config?: TenantConfig | null;
}

export function WeeklyOffers({ upcomingOffers, products, config }: WeeklyOffersProps) {
    const [activeDayIndex, setActiveDayIndex] = useState(0);
    const [activeTab, setActiveTab] = useState<'offers' | 'products'>('offers');

    // Generate next 7 days to ensure we show all days, not just those with offers
    const next7Days = Array.from({ length: 7 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() + i);
        return d;
    });

    const dayNames: Record<number, string> = {
        0: "Domingo", 1: "Lunes", 2: "Martes", 3: "Miércoles", 4: "Jueves", 5: "Viernes", 6: "Sábado"
    };

    const allDays: WeeklyOfferDay[] = next7Days.map(dateObj => {
        const dateStr = dateObj.toISOString().split('T')[0];
        const dayOfWeek = dateObj.getDay();

        // Find existing data from API
        // Compare dates robustly (string match yyyy-mm-dd)
        const existing = upcomingOffers.find(d => d.date === dateStr);

        if (existing) {
            // Filter out "announcement" offers: price 0 and no products
            const validOffers = existing.offers.filter(offer => {
                const hasProducts = offer.products && offer.products.length > 0;
                // Verify if it's an announcement (0 price, no products)
                if (offer.price === 0 && !hasProducts) {
                    return false;
                }
                return true;
            });

            return {
                ...existing,
                offers: validOffers
            };
        }

        return {
            date: dateStr,
            day_name: dayNames[dayOfWeek],
            offers: []
        };
    });

    // Calculate open status for each day
    const daysStatus = allDays.map(day => ({
        ...day,
        isOpen: config ? isDateWorkingDay(day.date, config) : true
    }));

    useEffect(() => {
        // Find the first open day to set as active, ONLY if we haven't set it yet or if current selection is invalid
        // But strictly speaking, we want to default to the first available/open day on mount.
        // We can check if activeDayIndex is pointing to a closed day or out of bounds.
        const currentDay = daysStatus[activeDayIndex];
        if (!currentDay || !currentDay.isOpen) {
            const firstOpenIndex = daysStatus.findIndex(day => day.isOpen);
            if (firstOpenIndex !== -1 && firstOpenIndex !== activeDayIndex) {
                setActiveDayIndex(firstOpenIndex);
            }
        }
    }, [upcomingOffers, config]); // dependencies might need review, but this is safe enough

    // REMOVED: if (daysWithOffers.length === 0) return null; - We want to show products even if no offers

    if (daysStatus.length === 0) {
        return null;
    }

    if (config && !config.accept_scheduled_orders) {
        return (
            <div className="mx-4 p-8 bg-rose-50 dark:bg-rose-900/10 border border-rose-200 dark:border-rose-800 rounded-3xl text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="w-16 h-16 bg-rose-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg text-white">
                    <Clock className="w-8 h-8" />
                </div>
                <div className="space-y-2">
                    <h3 className="text-xl font-black text-rose-900 dark:text-rose-100">Pedidos programados no disponibles</h3>
                    <p className="text-sm text-rose-700 dark:text-rose-300 font-medium">En este momento el restaurante no está aceptando pedidos programados.</p>
                </div>
            </div>
        );
    }

    // Safety check for empty offers logic
    const currentDay = daysStatus[activeDayIndex];

    // Determine availability for the current day
    const hasOffers = currentDay?.offers && currentDay.offers.length > 0;
    const hasProducts = products && products.length > 0;

    // Effect to auto-switch tab based on availability
    useEffect(() => {
        if (!currentDay) return;

        // If we have offers but no products (unlikely), force offers
        if (hasOffers && !hasProducts && activeTab !== 'offers') {
            setActiveTab('offers');
        }
        // If we have products but no offers, force products
        else if (!hasOffers && hasProducts && activeTab !== 'products') {
            setActiveTab('products');
        }
    }, [currentDay, hasOffers, hasProducts, activeTab]);

    if (!currentDay) return null;

    // Determine if we should show tabs
    const showTabs = hasOffers && hasProducts;

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between px-2">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-indigo-500" />
                    Calendario de Ofertas
                </h2>
            </div>

            <div className="space-y-6">
                {/* Days Navigation */}
                <div className="flex overflow-x-auto pb-2 gap-3 px-2 no-scrollbar snap-x">
                    {daysStatus.map((day, index) => {
                        const isActive = index === activeDayIndex;
                        const isDisabled = !day.isOpen;

                        return (
                            <button
                                key={day.date}
                                onClick={() => !isDisabled && setActiveDayIndex(index)}
                                disabled={isDisabled}
                                className={`flex-shrink-0 snap-start flex flex-col items-center justify-center p-3 min-w-[5rem] rounded-xl transition-all duration-300 border 
                                    ${isDisabled ? 'opacity-40 grayscale cursor-not-allowed bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700' : ''}
                                    ${isActive && !isDisabled
                                        ? `bg-indigo-500 text-white shadow-lg shadow-indigo-500/30 border-indigo-500 scale-105`
                                        : !isDisabled ? `bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700` : ''
                                    }`}
                            >
                                <span className={`text-xs font-medium uppercase tracking-wider mb-1 ${isActive && !isDisabled ? 'text-indigo-100' : ''}`}>
                                    {day.day_name.substring(0, 3)}
                                </span>
                                <span className="text-lg font-bold">
                                    {day.date.split('-')[2]}
                                </span>
                                {isActive && !isDisabled && (
                                    <div className="mt-1 w-1 h-1 bg-white rounded-full"></div>
                                )}
                            </button>
                        );
                    })}
                </div>

                {/* Tabs Selection - Only show if both options are available */}
                {showTabs && (
                    <div className={`flex p-1 bg-gray-100 dark:bg-gray-800 rounded-xl mx-2 transition-opacity duration-300 ${!currentDay.isOpen ? 'opacity-50 pointer-events-none' : ''}`}>
                        <button
                            onClick={() => setActiveTab('offers')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'offers'
                                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                }`}
                        >
                            <Sparkles className="w-4 h-4" />
                            Ofertas
                        </button>
                        <button
                            onClick={() => setActiveTab('products')}
                            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-bold transition-all ${activeTab === 'products'
                                ? 'bg-white dark:bg-gray-700 text-indigo-600 dark:text-indigo-400 shadow-sm'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
                                }`}
                        >
                            <UtensilsCrossed className="w-4 h-4" />
                            Productos
                        </button>
                    </div>
                )}

                {/* Content List for Active Day & Tab */}
                <div className="relative min-h-[12rem] px-2">
                    <div className="flex items-center gap-2 mb-3 text-sm text-gray-500 dark:text-gray-400">
                        {/* If tabs are hidden, activeTab value doesn't matter as much, 
                            but we rely on the computed visibility. 
                            If showTabs is false:
                                - if hasOffers: show offers header
                                - if hasProducts (and !hasOffers): show products header
                        */}
                        {showTabs ? (
                            activeTab === 'offers' ? (
                                <>
                                    <Sparkles className="w-4 h-4 text-amber-500" />
                                    <span>Ofertas para el {currentDay.day_name}</span>
                                </>
                            ) : (
                                <>
                                    <UtensilsCrossed className="w-4 h-4 text-indigo-500" />
                                    <span>Productos para el {currentDay.day_name}</span>
                                </>
                            )
                        ) : hasOffers ? (
                            <>
                                <Sparkles className="w-4 h-4 text-amber-500" />
                                <span>Ofertas para el {currentDay.day_name}</span>
                            </>
                        ) : (
                            <>
                                <UtensilsCrossed className="w-4 h-4 text-indigo-500" />
                                <span>Productos para el {currentDay.day_name}</span>
                            </>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        {/* Logic to render based on availability priority if tabs are hidden */}
                        {showTabs ? (
                            activeTab === 'offers' ? (
                                currentDay.offers.map((offer) => (
                                    <OfferCard
                                        key={offer.uuid}
                                        offer={offer}
                                        scheduledDate={currentDay.date}
                                    />
                                ))
                            ) : (
                                products.map((product) => (
                                    <ProductCard
                                        key={product.uuid}
                                        product={product}
                                        scheduledDate={currentDay.date}
                                    />
                                ))
                            )
                        ) : hasOffers ? (
                            currentDay.offers.map((offer) => (
                                <OfferCard
                                    key={offer.uuid}
                                    offer={offer}
                                    scheduledDate={currentDay.date}
                                />
                            ))
                        ) : (
                            products.map((product) => (
                                <ProductCard
                                    key={product.uuid}
                                    product={product}
                                    scheduledDate={currentDay.date}
                                />
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

